import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createLogger } from '../utils/logger';
import { apiFetch } from '../utils/api';

const log = createLogger('GeneratePage');

interface SubmissionResult {
  scan_id: string;
  job_id?: string;
  status: 'pending' | 'submitted' | 'error';
  error?: string;
}

type LicensePlateOption = 'no_plate' | 'bracket_only' | 'keep_plate';

const LP_OPTIONS: { value: LicensePlateOption; label: string }[] = [
  { value: 'no_plate', label: 'No plate' },
  { value: 'bracket_only', label: 'Bracket only' },
  { value: 'keep_plate', label: 'Keep plate' },
];

export function GeneratePage() {
  // Single scan state
  const [singleScanId, setSingleScanId] = useState('');
  const [singleSubmitting, setSingleSubmitting] = useState(false);
  const [singleResult, setSingleResult] = useState<SubmissionResult | null>(null);

  // Single scan settings
  const [photoboothName, setPhotoboothName] = useState('');
  const [lpFront, setLpFront] = useState<LicensePlateOption>('no_plate');
  const [lpRear, setLpRear] = useState<LicensePlateOption>('bracket_only');

  // Batch upload state
  const [batchFileName, setBatchFileName] = useState<string | null>(null);
  const [batchScanIds, setBatchScanIds] = useState<string[]>([]);
  const [batchResults, setBatchResults] = useState<SubmissionResult[]>([]);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Batch settings
  const [batchPhotoboothName, setBatchPhotoboothName] = useState('');
  const [batchLpFront, setBatchLpFront] = useState<LicensePlateOption>('no_plate');
  const [batchLpRear, setBatchLpRear] = useState<LicensePlateOption>('bracket_only');

  // Shared photobooth list
  const [photoboothNames, setPhotoboothNames] = useState<string[]>([]);

  // Backend defaults for license plate (used to decide when to send settings)
  const [lpDefaults, setLpDefaults] = useState<{ front: LicensePlateOption; rear: LicensePlateOption }>({ front: 'no_plate', rear: 'bracket_only' });

  useEffect(() => {
    apiFetch('/api/settings/photobooths')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setPhotoboothNames(
            Array.isArray(data) ? data.map((p: { name: string }) => p.name) : []
          );
        }
      })
      .catch((err) => log.error('Failed to fetch photobooths:', err));

    apiFetch('/api/settings/scan-defaults')
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          const front = (data?.license_plate?.front ?? 'no_plate') as LicensePlateOption;
          const rear = (data?.license_plate?.rear ?? 'bracket_only') as LicensePlateOption;
          setLpFront(front); setBatchLpFront(front);
          setLpRear(rear); setBatchLpRear(rear);
          setLpDefaults({ front, rear });
        }
      })
      .catch((err) => log.error('Failed to fetch scan defaults:', err));
  }, []);

  // Submit a single scan
  const submitSingleScan = async () => {
    if (!singleScanId.trim()) return;

    setSingleSubmitting(true);
    setSingleResult(null);

    try {
      const payload: Record<string, unknown> = {
        scan_id: singleScanId.trim(),
        source: 'manual',
      };
      if (photoboothName) payload.photobooth_name = photoboothName;
      const hasLpSettings = lpFront !== lpDefaults.front || lpRear !== lpDefaults.rear;
      if (hasLpSettings) {
        payload.settings = {
          license_plate: { front: lpFront, rear: lpRear },
        };
      }

      const response = await apiFetch('/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Single scan submitted: scan_id=${singleScanId.trim()}, job_id=${data.job_id}`);
        setSingleResult({
          scan_id: singleScanId.trim(),
          job_id: data.job_id,
          status: 'submitted',
        });
        setSingleScanId('');
      } else {
        const error = await response.text();
        log.error(`Single scan submission failed: scan_id=${singleScanId.trim()}, status=${response.status}`, error);
        setSingleResult({
          scan_id: singleScanId.trim(),
          status: 'error',
          error: error || 'Failed to submit scan',
        });
      }
    } catch (err) {
      log.error('Single scan submission network error:', err);
      setSingleResult({
        scan_id: singleScanId.trim(),
        status: 'error',
        error: err instanceof Error ? err.message : 'Network error',
      });
    } finally {
      setSingleSubmitting(false);
    }
  };

  // Parse CSV file - only uses FIRST column (scan ID / inspectionId)
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setBatchFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text) return;

      // Parse CSV - only take the FIRST column from each row
      const lines = text.split(/[\r\n]+/).filter(line => line.trim());
      const scanIds: string[] = [];

      for (const line of lines) {
        // Split by comma and take ONLY the first column
        const parts = line.split(',').map(part => part.trim());
        const firstColumn = parts[0];
        if (firstColumn) {
          scanIds.push(firstColumn);
        }
      }

      // Filter out headers or invalid entries (simple heuristic: must look like a UUID or ID)
      const validIds = scanIds.filter(id => {
        // Skip common header names
        const lowerCase = id.toLowerCase();
        if (['scan_id', 'scanid', 'id', 'scan', 'inspection_id', 'inspectionid'].includes(lowerCase)) {
          return false;
        }
        // Must have some alphanumeric content and be long enough
        return /^[a-zA-Z0-9-_]+$/.test(id) && id.length > 5;
      });

      setBatchScanIds(validIds);
      setBatchResults([]);
    };

    reader.readAsText(file);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Submit batch scans via batch API
  const submitBatchScans = async () => {
    if (batchScanIds.length === 0 || !batchFileName) return;

    setBatchSubmitting(true);
    setBatchProgress({ current: 0, total: batchScanIds.length });

    try {
      const batchPayload: Record<string, unknown> = {
        filename: batchFileName,
        scan_ids: batchScanIds,
      };
      if (batchPhotoboothName) batchPayload.photobooth_name = batchPhotoboothName;
      const hasBatchLpSettings = batchLpFront !== lpDefaults.front || batchLpRear !== lpDefaults.rear;
      if (hasBatchLpSettings) {
        batchPayload.settings = {
          license_plate: { front: batchLpFront, rear: batchLpRear },
        };
      }

      const response = await apiFetch('/api/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batchPayload),
      });

      if (response.ok) {
        const data = await response.json();
        log.info(`Batch submitted: batch_id=${data.batch_id}, scans=${batchScanIds.length}, filename=${batchFileName}`);
        // Mark all as submitted
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          job_id: data.batch_id,
          status: 'submitted' as const,
        }));
        setBatchResults(results);
        setBatchProgress({ current: batchScanIds.length, total: batchScanIds.length });
      } else {
        const error = await response.text();
        log.error(`Batch submission failed: status=${response.status}, scans=${batchScanIds.length}`, error);
        // Mark all as error
        const results: SubmissionResult[] = batchScanIds.map(scanId => ({
          scan_id: scanId,
          status: 'error' as const,
          error: error || 'Failed to submit batch',
        }));
        setBatchResults(results);
      }
    } catch (err) {
      log.error('Batch submission network error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      const results: SubmissionResult[] = batchScanIds.map(scanId => ({
        scan_id: scanId,
        status: 'error' as const,
        error: errorMsg,
      }));
      setBatchResults(results);
    } finally {
      setBatchSubmitting(false);
    }
  };

  // Clear batch
  const clearBatch = () => {
    setBatchFileName(null);
    setBatchScanIds([]);
    setBatchResults([]);
    setBatchProgress({ current: 0, total: 0 });
  };

  const successCount = batchResults.filter(r => r.status === 'submitted').length;
  const errorCount = batchResults.filter(r => r.status === 'error').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Single Scan Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Single Scan</h2>
            <p className="text-sm text-slate-500">Generate images for one scan ID</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="scanId" className="block text-sm font-medium text-slate-700 mb-2">
              Scan ID
            </label>
            <input
              id="scanId"
              type="text"
              value={singleScanId}
              onChange={(e) => setSingleScanId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitSingleScan()}
              placeholder="e.g., 57942aad-a26d-4b05-8c1b-7de5aa32326d"
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 font-mono text-sm transition-all duration-200"
            />
          </div>

          {/* Settings */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="photobooth" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
              <select
                id="photobooth"
                value={photoboothName}
                onChange={(e) => setPhotoboothName(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                <option value="">Server default</option>
                {photoboothNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lpFront" className="block text-xs font-medium text-slate-600 mb-1">Plate (Front)</label>
              <select
                id="lpFront"
                value={lpFront}
                onChange={(e) => setLpFront(e.target.value as LicensePlateOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {LP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lpRear" className="block text-xs font-medium text-slate-600 mb-1">Plate (Rear)</label>
              <select
                id="lpRear"
                value={lpRear}
                onChange={(e) => setLpRear(e.target.value as LicensePlateOption)}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
              >
                {LP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={submitSingleScan}
            disabled={!singleScanId.trim() || singleSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {singleSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate
              </>
            )}
          </button>

          {/* Single scan result */}
          {singleResult && (
            <div className={`p-4 rounded-lg ${
              singleResult.status === 'submitted'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {singleResult.status === 'submitted' ? (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Scan submitted successfully</p>
                    <p className="text-xs font-mono mt-1 text-slate-600">Job ID: {singleResult.job_id}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium">Failed to submit scan</p>
                    <p className="text-xs text-slate-600 mt-1">{singleResult.error}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Batch Upload Section */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-soft p-5 transition-all duration-200 hover:shadow-soft-hover">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Batch Upload</h2>
            <p className="text-sm text-slate-500">Generate images from a CSV file</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* File upload area */}
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-all duration-200">
              <svg className="w-12 h-12 mx-auto text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm text-slate-700">
                Drop a CSV file here or <span className="text-indigo-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                One scan ID per line or comma-separated
              </p>
            </div>
          </div>

          {/* Scan IDs preview */}
          {batchScanIds.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600">
                    <span className="text-slate-900 font-semibold">{batchScanIds.length}</span> scan IDs loaded
                  </span>
                  {batchFileName && (
                    <span className="text-xs text-slate-400 font-mono">({batchFileName})</span>
                  )}
                </div>
                <button
                  onClick={clearBatch}
                  className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Clear all
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {batchScanIds.slice(0, 10).map((id, idx) => (
                  <div key={idx} className="text-xs font-mono text-slate-500 truncate">
                    {id}
                  </div>
                ))}
                {batchScanIds.length > 10 && (
                  <div className="text-xs text-slate-400">
                    ... and {batchScanIds.length - 10} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Batch settings */}
          {batchScanIds.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="batchPhotobooth" className="block text-xs font-medium text-slate-600 mb-1">Background</label>
                <select
                  id="batchPhotobooth"
                  value={batchPhotoboothName}
                  onChange={(e) => setBatchPhotoboothName(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  <option value="">Server default</option>
                  {photoboothNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="batchLpFront" className="block text-xs font-medium text-slate-600 mb-1">Plate (Front)</label>
                <select
                  id="batchLpFront"
                  value={batchLpFront}
                  onChange={(e) => setBatchLpFront(e.target.value as LicensePlateOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {LP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="batchLpRear" className="block text-xs font-medium text-slate-600 mb-1">Plate (Rear)</label>
                <select
                  id="batchLpRear"
                  value={batchLpRear}
                  onChange={(e) => setBatchLpRear(e.target.value as LicensePlateOption)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all duration-200"
                >
                  {LP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Submit batch button */}
          <button
            onClick={submitBatchScans}
            disabled={batchScanIds.length === 0 || batchSubmitting}
            className="btn-primary w-full px-4 py-2.5 text-sm flex items-center justify-center gap-2"
          >
            {batchSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting {batchProgress.current} / {batchProgress.total}...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Submit Batch ({batchScanIds.length} scans)
              </>
            )}
          </button>

          {/* Batch results summary */}
          {batchResults.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-900">Submission Results</span>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-green-600">{successCount} submitted</span>
                  {errorCount > 0 && <span className="text-red-600">{errorCount} failed</span>}
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-200 rounded-full h-2 mb-3 overflow-hidden">
                <div
                  className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${(successCount / batchResults.length) * 100}%` }}
                />
              </div>

              {/* Show errors if any */}
              {errorCount > 0 && (
                <div className="max-h-24 overflow-y-auto space-y-1">
                  {batchResults
                    .filter(r => r.status === 'error')
                    .slice(0, 5)
                    .map((result, idx) => (
                      <div key={idx} className="text-xs text-red-600 truncate">
                        {result.scan_id}: {result.error}
                      </div>
                    ))}
                </div>
              )}

              <Link
                to="/lightning/live"
                className="mt-4 block w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-center text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                View Progress in Live Scans
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
